import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY

// DEV MODE: When true, auth is bypassed with a mock user (see AuthProvider)
// In production, set VITE_DEV_MODE to anything other than 'true' (or omit it)
const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_MODE === 'true'

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// In dev mode, use the service role key to bypass RLS (no real auth session).
// In production, always use the anon key — service role key must NEVER ship to prod.
const apiKey = DEV_BYPASS_AUTH && supabaseServiceRoleKey
  ? supabaseServiceRoleKey
  : supabaseAnonKey

// No-op lock — works around a deadlock in supabase-js between getSession()
// and the internal init step that causes infinite Loading screens (see
// commit 49c34b3). Stays on until @supabase/supabase-js is actually bumped
// past the fix; set VITE_SUPABASE_DISABLE_LOCK=false ONLY after that upgrade
// is verified end-to-end.
const DISABLE_AUTH_LOCK = import.meta.env.VITE_SUPABASE_DISABLE_LOCK !== 'false'
const navigatorLockNoOp = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  return await fn()
}

// Custom fetch with timeouts — prevents requests from hanging forever when
// the browser's TCP connection dies after a long idle period.
//
// Three tiers:
//   - Long-running edge functions: no timeout (caller passes their own
//     AbortSignal — Monday sync, vendor sync).
//   - Auth (`/auth/v1/*`): 60s. Previously had no timeout at all, but a
//     stalled refresh would hang refreshSession() forever, latching the
//     AuthProvider's isRefreshing flag and freezing React Query's retry
//     callbacks. 60s is generous enough that a normal refresh on a slow
//     mobile network completes, but bounds the worst case so the rest of
//     the app can recover.
//   - Everything else: 30s.
const LONG_RUNNING_ENDPOINTS = ['/functions/v1/monday-board-sync', '/functions/v1/vendor-sync']
const AUTH_PATHS = ['/auth/v1/']

const fetchWithTimeout: typeof fetch = (input, init) => {
  const url = typeof input === 'string' ? input : (input as Request).url || String(input)
  const isLongRunning = LONG_RUNNING_ENDPOINTS.some(p => url.includes(p))
  if (isLongRunning) {
    return fetch(input, init)
  }
  const isAuth = AUTH_PATHS.some(p => url.includes(p))
  const timeoutMs = isAuth ? 60_000 : 30_000

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  const signal = init?.signal
    ? // Respect caller's signal too (abort on either)
      AbortSignal.any
        ? AbortSignal.any([init.signal, controller.signal])
        : controller.signal
    : controller.signal

  return fetch(input, { ...init, signal }).finally(() => clearTimeout(timeout))
}

// Every REST / storage / functions call resolves its JWT via
// auth.getSession(), and supabase-js queues that behind any in-flight
// refreshSession(). AuthProvider refreshes on the first tap after 90s idle and
// on tab focus (e.g. back from the camera), so a save made right then waited
// on the refresh — up to the 60s auth timeout on a stalled mobile connection
// ("receipt won't submit after the page sat"). If getSession() doesn't answer
// quickly, use the persisted token while it is still valid; only an expired
// token keeps waiting for the refresh.
const TOKEN_WAIT_MS = 2_000
type TokenGetter = { _getAccessToken?: () => Promise<string | null> }
const proto = SupabaseClient.prototype as unknown as TokenGetter
const originalGetAccessToken = proto._getAccessToken
if (typeof originalGetAccessToken === 'function') {
  proto._getAccessToken = function (this: unknown) {
    const pending = originalGetAccessToken.call(this)
    return Promise.race([
      pending,
      new Promise<string | null>((resolve) =>
        setTimeout(() => resolve(storedTokenIfValid() ?? pending), TOKEN_WAIT_MS),
      ),
    ])
  }
}

function storedTokenIfValid(): string | null {
  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as { access_token?: unknown; expires_at?: unknown }
    if (typeof s.access_token !== 'string' || typeof s.expires_at !== 'number') return null
    // 30s margin so the request doesn't land just after expiry.
    return s.expires_at * 1000 - Date.now() > 30_000 ? s.access_token : null
  } catch {
    return null
  }
}

export const supabase = createClient<Database>(supabaseUrl, apiKey, {
  auth: {
    // Background auto-refresh is DISABLED on purpose. supabase-js's refresh
    // ticker fires token fetches even while the tab is frozen/backgrounded; a
    // frozen fetch never settles and latches the client's internal refresh
    // promise. The NEXT refreshSession() (on tab focus) then awaits that dead
    // promise and hangs forever — surfacing as "refreshSession timed out" and a
    // page that loads only static content after a long idle. We drive refresh
    // ourselves from AuthProvider (visibility/focus/3-min interval/activity),
    // always while the tab is VISIBLE so those fetches actually complete.
    autoRefreshToken: false,
    persistSession: !DEV_BYPASS_AUTH,
    detectSessionInUrl: !DEV_BYPASS_AUTH,
    // Implicit flow until supabase-js is upgraded — PKCE was introduced in
    // commit 34d3040 without a package bump and caused detectSessionInUrl
    // to contend with the lock deadlock. Revisit alongside the supabase-js
    // upgrade.
    flowType: 'implicit',
    // Apply the no-op lock by default to avoid the navigator.locks deadlock.
    ...(DISABLE_AUTH_LOCK ? { lock: navigatorLockNoOp } : {}),
  },
  global: {
    fetch: fetchWithTimeout,
  },
})

// Storage key used by Supabase auth — exported for manual cleanup on lock hangs
const supabaseRef = supabaseUrl.match(/\/\/([^.]+)/)?.[1] ?? 'unknown'
export const SUPABASE_STORAGE_KEY = `sb-${supabaseRef}-auth-token`

/**
 * Safe wrapper around supabase.auth.refreshSession() with a hard timeout.
 *
 * Even though the underlying fetch now has a 60s bound, this adds belt-and-
 * suspenders protection in case the supabase-js client gets stuck on its own
 * navigator.locks or internal state machine. Without this, the React Query
 * retry callback (App.tsx) and AuthProvider's refreshIfNeeded can await an
 * unresolving promise forever, freezing the UI.
 *
 * Returns { data: { session }, error } matching supabase-js's shape. A
 * timeout resolves to `{ data: { session: null }, error: { message } }` so
 * callers don't need to wrap with try/catch — they treat it like any other
 * refresh error (no retry, log + move on).
 */
const REFRESH_TIMEOUT_MS = 15_000

export async function refreshSessionSafe(): Promise<{
  data: { session: Awaited<ReturnType<typeof supabase.auth.refreshSession>>['data']['session'] | null }
  error: { message: string } | null
}> {
  try {
    const result = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise<{ data: { session: null }; error: { message: string } }>((resolve) =>
        setTimeout(
          () =>
            resolve({
              data: { session: null },
              error: { message: `refreshSession timed out after ${REFRESH_TIMEOUT_MS}ms` },
            }),
          REFRESH_TIMEOUT_MS,
        ),
      ),
    ])
    return result as Awaited<ReturnType<typeof supabase.auth.refreshSession>>
  } catch (err) {
    return {
      data: { session: null },
      error: { message: err instanceof Error ? err.message : String(err) },
    }
  }
}

/**
 * Resolve the current access token WITHOUT risking an indefinite hang.
 *
 * supabase-js resolves the token for every REST / functions.invoke() call via
 * auth.getSession(). When the stored JWT has expired, getSession() awaits the
 * client's internal refresh promise — and when that promise is wedged (the
 * "refreshSession timed out" console warnings), the await never settles, so
 * the caller hangs forever with no error. This races getSession() against a
 * short timeout and falls back to the persisted session in localStorage (a
 * stale token yields a fast 401, which beats an infinite spinner), then the
 * anon key.
 */
const TOKEN_TIMEOUT_MS = 4_000

export async function getAccessTokenSafe(timeoutMs = TOKEN_TIMEOUT_MS): Promise<string> {
  const fromClient = await Promise.race([
    supabase.auth
      .getSession()
      .then(({ data }) => data.session?.access_token ?? null)
      .catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ])
  if (fromClient) return fromClient
  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { access_token?: unknown }
      if (typeof parsed.access_token === 'string' && parsed.access_token) return parsed.access_token
    }
  } catch {
    /* ignore — fall through to the anon key */
  }
  return apiKey
}

export { DEV_BYPASS_AUTH }
export type { Database }
